import { getUserId } from "./userId";

const GAME_NAME = "warmle";
const API_BASE = "https://state.warmle.org";

export interface RemoteGameState {
  user_id?: string;
  updated_at?: string;
  seconds_since_update?: number;
  metadata: {
    status: "in_progress" | "won" | "lost";
    numGuesses: number;
    puzzleId: string;
  };
  state: {
    guesses: { word: string; clues: [] }[];
    currentGuess: string;
    cursorPosition: number;
  };
}

export interface WatchableGames {
  liveGames: RemoteGameState[];
  replayGames: RemoteGameState[];
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export function saveGameState(
  userId: string,
  puzzleId: string,
  payload: RemoteGameState
): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    fetch(`${API_BASE}/state/${userId}/${GAME_NAME}/${puzzleId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => {
        if (!r.ok) console.warn("saveGameState: HTTP", r.status, r.statusText);
      })
      .catch((err) => console.warn("Failed to sync game state:", err));
  }, 300);
}

export async function findWatchableGames(
  puzzleId: string,
  excludeUserId: string
): Promise<WatchableGames> {
  const result: WatchableGames = { liveGames: [], replayGames: [] };
  try {
    const liveRes = await fetch(
      `${API_BASE}/states/${GAME_NAME}?puzzle_id=${encodeURIComponent(puzzleId)}&max_age=40&status=in_progress&limit=100`
    );
    if (liveRes.ok) {
      const liveStates = (await liveRes.json()) as RemoteGameState[];
      if (liveStates) {
        result.liveGames = liveStates
          .filter((s) => s.user_id !== excludeUserId)
          .sort((a, b) => a.metadata.numGuesses - b.metadata.numGuesses);
      }
    }

    const replayRes = await fetch(
      `${API_BASE}/states/${GAME_NAME}?puzzle_id=${encodeURIComponent(puzzleId)}&limit=100`
    );
    if (replayRes.ok) {
      const replayStates = (await replayRes.json()) as RemoteGameState[];
      if (replayStates) {
        result.replayGames = replayStates.filter(
          (s) =>
            s.user_id !== excludeUserId &&
            (s.metadata.status === "won" || s.metadata.status === "lost")
        );
      }
    }
  } catch (err) {
    console.warn("Failed to find watchable games:", err);
  }
  return result;
}

export async function longPollGameState(
  userId: string,
  puzzleId: string,
  after: string | null,
  signal?: AbortSignal
): Promise<RemoteGameState | null> {
  try {
    const params = new URLSearchParams({ watch: "true", timeout: "20" });
    if (after) params.set("after", after);
    const res = await fetch(
      `${API_BASE}/state/${userId}/${GAME_NAME}/${puzzleId}?${params}`,
      { signal }
    );
    if (res.status === 304) return null;
    if (!res.ok) return null;
    return (await res.json()) as RemoteGameState;
  } catch (err) {
    if (signal?.aborted) return null;
    console.warn("Long-poll failed:", err);
    return null;
  }
}
